export interface TodoStatusOption {
	id: number;
	label?: string;
	name?: string;
}

const DONE_STATUS_LABELS = new Set(['done', 'completed', 'complete']);

export const normalizeTodoStatusList = (data: unknown): TodoStatusOption[] => {
	if (Array.isArray(data)) {
		return data.filter((item): item is TodoStatusOption => item != null && typeof item === 'object' && 'id' in item);
	}

	if (data && typeof data === 'object') {
		const record = data as Record<string, unknown>;
		const nested = record.member ?? record['hydra:member'] ?? record.items ?? record.data;
		if (Array.isArray(nested)) {
			return normalizeTodoStatusList(nested);
		}
	}

	return [];
};

export const findDoneStatusId = (statuses: TodoStatusOption[]): number | null => {
	for (const status of statuses) {
		const label = String(status.label ?? status.name ?? '').trim().toLowerCase();
		if (DONE_STATUS_LABELS.has(label)) {
			return Number(status.id);
		}
	}
	return null;
};

export const isDoneStatusId = (statusId: number, statuses: TodoStatusOption[]): boolean => {
	const doneStatusId = findDoneStatusId(statuses);
	return doneStatusId != null && Number(statusId) === Number(doneStatusId);
};

export const getTodoCompletionFields = (currentUserId: number): {
	completedBy: string;
	completedAt: string;
} => ({
	completedBy: `/api/users/${currentUserId}`,
	completedAt: new Date().toISOString(),
});

export const withTodoCompletionFieldsIfDone = <T extends Record<string, unknown>>(
	body: T,
	statusId: number,
	statuses: TodoStatusOption[],
	currentUserId: number
): T & Partial<{ completedBy: string; completedAt: string }> => {
	if (!isDoneStatusId(statusId, statuses) || !Number.isFinite(currentUserId) || currentUserId <= 0) {
		return body;
	}

	return {
		...body,
		...getTodoCompletionFields(currentUserId),
	};
};
